import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Container,
  Paper,
  Tab,
  Tabs,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Modal
} from '@mui/material'
import { Link, useLocation } from 'react-router-dom'
import {
  HiOutlineUserGroup,
  HiOutlineDocumentText,
  HiOutlineTicket,
  HiOutlineQuestionMarkCircle,
  HiOutlineClipboardList,
  HiOutlineCog,
  HiOutlineGlobeAlt,
  HiOutlineCreditCard,
  HiOutlineCheckCircle,
  HiOutlineExclamation
} from 'react-icons/hi'
import logoImage_1 from '../../assets/White PNG 1.png'
import logoImage_2 from '../../assets/Black PNG 1.png'
import './AdminPanel.scss'
import moment from 'moment-jalaali'
import 'dayjs/locale/fa'
import InvoicesTab from './InvoicesTab'
import UsersTab from './UsersTab'
import PlansTab from './PlansTab'
import DiscountsTab from './DiscountsTab'
import ContentTab from './ContentTab'
import SettingsTab from './SettingsTab'
import SiteSettingsTab from './SiteSettingsTab'
import TradeAccountsTab from './TradeAccountsTab'
import TicketsList from '../Ticket/tickets_list'
import TicketDetails from '../Ticket/ticket_details'

moment.loadPersian({ usePersianDigits: true })

interface User {
  id: number
  username: string
  first_name: string
  last_name: string
  email: string
  phone: string
  wallet: number
  referral_commission: number
}
interface Plan {
  id: number
  name: string
  title: string
  min_price: number
  ratio: number
  durations: { duration: number }[]
  isActive: boolean
  plan_option_id?: number
}
interface PlanOptionInfo {
  id: number
  title: string
}
interface Faq {
  id: number
  question: string
  answer: string
}
interface Rule {
  content: string
}
interface CommissionTier {
  target: number
  commission_rate: number
}
interface UserCommission {
  user: {
    id: number
    first_name: string
    last_name: string
    username: string
  }
  data: CommissionTier[]
}
interface CommissionSettings {
  default: CommissionTier[]
  users: UserCommission[]
}
interface ReferralDiscount {
  user_id: number
  discount: number
  discount_count: number
  expire_at: number | null
}
type AdminPermissions = {
  change_settings: boolean
  change_user_settings: boolean
  manage_admins: boolean
  ticket_access: boolean
}
interface AdminApiResponse {
  id: number
  username: string
  first_name: string
  last_name: string
  email: string
  admin: {
    id: number
    permissions: AdminPermissions
  }
}
interface SitePhoto {
  id: number
  url: string
}
interface SiteSettings {
  site_title: string
  site_description: string
  email: string
  about_us: string
  telegram_link: string
  instagram_link: string
  youtube_link: string
  castbox_link: string
  site_photos?: SitePhoto[]
}

const apiFetch = async (url: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('token')
  const isFormData = options.body instanceof FormData
  const headers: HeadersInit = { Authorization: `Bearer ${token}`, ...options.headers }
  if (!isFormData) {
    headers['Content-Type'] = 'application/json'
  }
  const response = await fetch(`https://api.neontradepro.com${url}`, { ...options, headers })

  if (!response.ok) {
    try {
      const errorData = await response.json()
      let errorMessage = 'خطایی رخ داد.'

      if (errorData.detail && Array.isArray(errorData.detail)) {
        errorMessage = errorData.detail.map((err: any) => `فیلد '${err.loc[1]}': ${err.msg}`).join(' | ')
      } else if (typeof errorData.detail === 'string') {
        errorMessage = errorData.detail
      } else if (errorData.error) {
        errorMessage = errorData.error
      } else {
        errorMessage = response.statusText
      }

      throw new Error(errorMessage)
    } catch (e) {
      if (e instanceof Error) {
        throw e
      }
      throw new Error(response.statusText || 'خطا در ارتباط با سرور')
    }
  }

  const contentType = response.headers.get('content-type')
  if (contentType && contentType.indexOf('application/json') !== -1) {
    const text = await response.text()
    return text ? JSON.parse(text) : {}
  }
  return {}
}

function TabPanel(props: any) {
  const { children, value, index, ...other } = props
  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>{children}</Box>}
    </div>
  )
}
function ViewSettingsComponent({
  loading,
  settings,
  options,
  planName
}: {
  loading: boolean
  settings: any
  options: any
  planName: string
}) {
  if (loading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    )
  if (!settings || settings.notConfigured) return <Typography>تنظیماتی برای این پلن ثبت نشده است.</Typography>
  if (settings.error) return <Alert severity='error'>{settings.error}</Alert>
  if (!options) return <pre>{JSON.stringify(settings, null, 2)}</pre>

  const renderFieldValue = (fieldValue: any, fieldOption: any) => {
    if (Array.isArray(fieldValue)) {
      return fieldValue.join(', ')
    }
    if (typeof fieldValue === 'boolean') {
      return fieldValue ? 'فعال' : 'غیرفعال'
    }
    if (fieldOption.type === 'combine') {
      let combinedString = ''
      fieldOption.data?.forEach((item: any) => {
        if (typeof item === 'string') {
          combinedString += item
        } else if (item.id && fieldValue[item.id] !== undefined) {
          combinedString += ` ${fieldValue[item.id]} `
        }
      })
      return combinedString.trim()
    }
    return String(fieldValue)
  }

  const renderSetting = (key: string, option: any, value: any) => {
    const displayLabel = option.label || option.title || key

    if (option.type === 'object' && value) {
      return (
        <Box key={key} sx={{ mb: 2, p: 2, borderRadius: '8px', background: 'rgba(0,0,0,0.1)' }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
            {displayLabel}
          </Typography>
          <Box sx={{ pl: 2, mt: 1 }}>
            {option.properties &&
              Object.keys(option.properties).map(propKey => {
                const propOption = option.properties[propKey]
                const propValue = value[propKey]
                if (propValue === undefined) return null
                return (
                  <Typography key={propKey} variant='body2'>
                    <strong>{propOption.label || propOption.title || propKey}:</strong>{' '}
                    {renderFieldValue(propValue, propOption)}
                  </Typography>
                )
              })}
          </Box>
        </Box>
      )
    }

    if (value === undefined) return null

    return (
      <Typography key={key} variant='body2' sx={{ mb: 1 }}>
        <strong>{displayLabel}:</strong> {renderFieldValue(value, option)}
      </Typography>
    )
  }

  return (
    <Box>
      <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
        تنظیمات ذخیره شده برای پلن "{planName}"
      </Typography>
      {Object.keys(options).map(key => {
        const option = options[key]
        const fieldKey = Array.isArray(options) 
            ? (option.name || option.key || option.id || option.slug || key) 
            : key;
            
        const value = settings[fieldKey];
        if (value === undefined) return null;

        return renderSetting(key, option, value)
      })}
    </Box>
  )
}

const AdminPanel: React.FC<{ theme: string }> = ({ theme }) => {
  const location = useLocation()
  const getTabValueFromUrl = useCallback(() => {
    if (location.pathname.startsWith('/admin/tickets')) {
      return 8
    }
    const params = new URLSearchParams(location.search)
    return parseInt(params.get('tab') || '0', 10)
  }, [location.pathname, location.search])

  const [tabValue, setTabValue] = useState(getTabValueFromUrl)
  const [error, setError] = useState<string | null>(null)
  const [allUsers, setAllUsers] = useState<User[]>([])
  const [usersPage, setUsersPage] = useState(1)
  const [usersTotalPages, setUsersTotalPages] = useState(1)
  const [usersLoading, setUsersLoading] = useState(true)
  const [searchResults, setSearchResults] = useState<User[]>([])
  const [plans, setPlans] = useState<Plan[]>([])
  const [planOptions, setPlanOptions] = useState<{ [planId: number]: any }>({})
  const [allPlanOptions, setAllPlanOptions] = useState<PlanOptionInfo[]>([])
  const [faqs, setFaqs] = useState<Faq[]>([])
  const [rule, setRule] = useState<Rule>({ content: '' })
  const [admins, setAdmins] = useState<AdminApiResponse[]>([])
  const [commissionSettings, setCommissionSettings] = useState<CommissionSettings>({ default: [], users: [] })
  const [referralDiscounts, setReferralDiscounts] = useState<ReferralDiscount[]>([])

  const [siteSettings, setSiteSettings] = useState<Partial<SiteSettings>>({})

  const [loading, setLoading] = useState({
    usersSearch: false,
    plans: true,
    faqs: true,
    rule: true,
    settings: true,
    admins: true,
    siteSettings: true
  })

  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [successModalMessage, setSuccessModalMessage] = useState('')
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [confirmModalMessage, setConfirmModalMessage] = useState('')
  const [onConfirmAction, setOnConfirmAction] = useState<(() => void) | null>(null)

  const [editingAdmin, setEditingAdmin] = useState<AdminApiResponse | null>(null)
  const [editedAdminPermissions, setEditedAdminPermissions] = useState<AdminPermissions>({
    change_settings: false,
    change_user_settings: false,
    manage_admins: false,
    ticket_access: false
  })
  const [adminSaveLoading, setAdminSaveLoading] = useState(false)

  const [viewSettingsOpen, setViewSettingsOpen] = useState<boolean>(false)
  const [settingsToView, setSettingsToView] = useState<any>(null)
  const [optionsForView, setOptionsForView] = useState<any>(null)
  const [viewLoading, setViewLoading] = useState<boolean>(false)
  const [activePlanName, setActivePlanName] = useState<string>('')

  useEffect(() => {
    document.body.setAttribute('data-theme', theme)
  }, [theme])

  useEffect(() => {
    setTabValue(getTabValueFromUrl())
  }, [location.pathname, location.search])

  const fetchAllUsers = useCallback(async (page: number) => {
    setUsersLoading(true)
    try {
      const response = await apiFetch(`/admin/users?page=${page}&size=50`)
      setAllUsers(response.items || [])
      setUsersTotalPages(response.pages || 1)
      setUsersPage(response.page || 1)
    } catch (err: any) {
      setError(prev => (prev ? `${prev} | خطای کاربران: ${err.message}` : `خطای کاربران: ${err.message}`))
    } finally {
      setUsersLoading(false)
    }
  }, [])

  const fetchData = useCallback(async () => {
    setError(null)
    setLoading(prev => ({
      ...prev,
      plans: true,
      faqs: true,
      rule: true,
      settings: true,
      admins: true,
      siteSettings: true
    }))

    apiFetch('/settings/site')
      .then(data => setSiteSettings(data || {}))
      .catch(err => setError(`خطا در بارگذاری تنظیمات سایت: ${err.message}`))
      .finally(() => setLoading(prev => ({ ...prev, siteSettings: false })))

    apiFetch('/admin/')
      .then(response => {
        const adminList = response
        setAdmins(Array.isArray(adminList) ? adminList : [])
      })
      .catch(err => setError(`خطا در بارگذاری لیست ادمین‌ها: ${err.message}`))
      .finally(() => setLoading(prev => ({ ...prev, admins: false })))

    try {
      const [apiPlans, allOpts] = await Promise.all([apiFetch('/plan/'), apiFetch('/plan/options')])

      const fetchedPlans = Array.isArray(apiPlans)
        ? apiPlans
        : apiPlans.data && Array.isArray(apiPlans.data)
        ? apiPlans.data
        : []
      setPlans(fetchedPlans)

      const optionsList = allOpts.items || allOpts.data || (Array.isArray(allOpts) ? allOpts : [])
      if (Array.isArray(optionsList)) {
        setAllPlanOptions(optionsList)
      }

      const optionsPromises = fetchedPlans.map(async plan => {
        if (plan.plan_option_id) {
          try {
            const optionsResponse = await apiFetch(`/plan/options/${plan.plan_option_id}`)
            if (optionsResponse && optionsResponse.data) {
              return { planId: plan.id, options: optionsResponse.data }
            }
          } catch (e) {
            console.error(`Could not fetch options for plan ${plan.id}:`, e)
            return { planId: plan.id, options: {} }
          }
        }
        return { planId: plan.id, options: {} }
      })

      const optionsResults = await Promise.all(optionsPromises)
      const optionsMap = optionsResults.reduce((acc, result) => {
        if (result) {
          acc[result.planId] = result.options
        }
        return acc
      }, {} as { [planId: number]: any })

      setPlanOptions(optionsMap)
    } catch (err: any) {
      setError(`خطا در بارگذاری پلن‌ها: ${err.message}`)
    } finally {
      setLoading(prev => ({ ...prev, plans: false }))
    }

    apiFetch('/faqs/')
      .then(data => setFaqs(Array.isArray(data) ? data : []))
      .catch(err => setError(`خطا در بارگذاری سوالات متداول: ${err.message}`))
      .finally(() => setLoading(prev => ({ ...prev, faqs: false })))

    apiFetch('/rule/')
      .then(data => setRule(data))
      .catch(err => setError(`خطا در بارگذاری قوانین: ${err.message}`))
      .finally(() => setLoading(prev => ({ ...prev, rule: false })))

    const commissionPromise = apiFetch('/settings/commission-rate').catch(e => {
      console.warn('Could not fetch commission settings (likely not set yet):', e.message)
      return null
    })
    const referralPromise = apiFetch('/settings/referral-discounts/').catch(e => {
      console.error('Could not fetch referral discounts:', e)
      setError('خطا در بارگذاری تخفیف‌های معرف')
      return null
    })

    Promise.all([commissionPromise, referralPromise])
      .then(([commissionData, referralData]) => {
        if (commissionData) {
          setCommissionSettings(commissionData)
        }
        if (referralData) {
          setReferralDiscounts(Array.isArray(referralData) ? referralData : [])
        }
      })
      .finally(() => {
        setLoading(prev => ({ ...prev, settings: false }))
      })
  }, [])

  useEffect(() => {
    fetchData()
    fetchAllUsers(1)
  }, [fetchData, fetchAllUsers])

  const handleApiCall = async (apiCall: Promise<any>, successMessage: string, clearError = true) => {
    if (clearError) setError(null)
    try {
      const response = await apiCall
      setSuccessModalMessage(successMessage)
      setSuccessModalOpen(true)
      fetchData()
      return response
    } catch (err: any) {
      setError(err.message)
      throw err
    }
  }

  const showNotificationModal = (message: string) => {
    setSuccessModalMessage(message)
    setSuccessModalOpen(true)
  }

  const showConfirmationModal = (message: string, onConfirm: () => void) => {
    setConfirmModalMessage(message)
    setOnConfirmAction(() => onConfirm)
    setConfirmModalOpen(true)
  }

  const handleCloseConfirmModal = () => {
    setConfirmModalOpen(false)
    setConfirmModalMessage('')
    setOnConfirmAction(null)
  }

  const handleConfirm = () => {
    if (onConfirmAction) {
      onConfirmAction()
    }
    handleCloseConfirmModal()
  }

  const handleOpenEditAdminModal = (admin: AdminApiResponse) => {
    setEditingAdmin(admin)
    setEditedAdminPermissions(admin.admin.permissions)
  }

  const handleCloseEditAdminModal = () => {
    setEditingAdmin(null)
  }

  const handleEditedPermissionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditedAdminPermissions({
      ...editedAdminPermissions,
      [event.target.name]: event.target.checked
    })
  }

  const handleUpdateAdmin = async () => {
    if (!editingAdmin) return
    const payload = {
      user_id: editingAdmin.id,
      permissions: editedAdminPermissions
    }
    setAdminSaveLoading(true)
    try {
      await handleApiCall(
        apiFetch('/admin/', { method: 'PUT', body: JSON.stringify(payload) }),
        `مجوزهای ادمین ${editingAdmin.username} با موفقیت ویرایش شد.`
      )
      handleCloseEditAdminModal()
    } catch (err) {
    } finally {
      setAdminSaveLoading(false)
    }
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
    const params = new URLSearchParams(location.search)
    if (newValue === 0) {
      params.delete('tab')
    } else {
      params.set('tab', newValue.toString())
    }
    window.history.replaceState({}, '', `/admin${params.toString() ? `?${params.toString()}` : ''}`)
  }

  function RenderAdminTickets() {
    if (/\/admin\/tickets\/\d+/.test(location.pathname)) {
      const ticketId = location.pathname.split('/')[3]
      return <TicketDetails ticket_id={ticketId} isAdmin />
    }
    return <TicketsList isAdmin />
  }

  const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 400 },
    bgcolor: 'var(--background-color)',
    color: 'var(--text-color)',
    border: '2px solid var(--border-color)',
    boxShadow: 24,
    p: 4,
    borderRadius: '12px',
    textAlign: 'center'
  }

  return (
    <Container maxWidth='lg' sx={{ mt: 4, mb: 4 }} className='admin-panel-wrapper'>
      <Modal open={viewSettingsOpen} onClose={() => setViewSettingsOpen(false)}>
        <Box
          sx={{
            maxWidth: '90%',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            bgcolor: 'var(--background-color-secondary)',
            borderRadius: '10px',
            boxShadow: 24,
            p: 4,
            color: 'var(--text-color)',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}
        >
          <ViewSettingsComponent
            loading={viewLoading}
            settings={settingsToView}
            options={optionsForView}
            planName={activePlanName}
          />
        </Box>
      </Modal>

      <Modal open={successModalOpen} onClose={() => setSuccessModalOpen(false)}>
        <Box sx={modalStyle}>
          <HiOutlineCheckCircle size={48} style={{ color: 'var(--success-color)', marginBottom: '16px' }} />
          <Typography variant='h6'sx={{ mt: 2, mb: 3 }}>عملیات موفق آمیز بود</Typography>
          <Button variant='contained' onClick={() => setSuccessModalOpen(false)}>
            فهمیدم
          </Button>
        </Box>
      </Modal>

      <Modal open={confirmModalOpen} onClose={handleCloseConfirmModal}>
        <Box sx={modalStyle}>
          <HiOutlineExclamation size={48} style={{ color: 'var(--warning-color)', marginBottom: '16px' }} />
          <Typography variant='h6'>تایید عملیات</Typography>
          <Typography sx={{ mt: 2, mb: 3 }}>{confirmModalMessage}</Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant='outlined' color='secondary' onClick={handleCloseConfirmModal}>
              لغو
            </Button>
            <Button variant='contained' color='error' onClick={handleConfirm}>
              تایید
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={!!editingAdmin} onClose={handleCloseEditAdminModal}>
        <Box sx={modalStyle}>
          <Typography variant='h6'>ویرایش مجوزهای ادمین</Typography>
          {editingAdmin && (
            <Typography sx={{ mt: 1, mb: 2 }}>
              <strong>{editingAdmin.username}</strong> (ID: {editingAdmin.id})
            </Typography>
          )}
          <FormGroup sx={{ display: 'flex', alignItems: 'flex-start', textAlign: 'left', mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editedAdminPermissions.change_settings}
                  onChange={handleEditedPermissionChange}
                  name='change_settings'
                />
              }
              label='تغییر تنظیمات'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editedAdminPermissions.change_user_settings}
                  onChange={handleEditedPermissionChange}
                  name='change_user_settings'
                />
              }
              label='تغییر تنظیمات کاربر'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editedAdminPermissions.manage_admins}
                  onChange={handleEditedPermissionChange}
                  name='manage_admins'
                />
              }
              label='مدیریت ادمین‌ها'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editedAdminPermissions.ticket_access}
                  onChange={handleEditedPermissionChange}
                  name='ticket_access'
                />
              }
              label='دسترسی به تیکت‌ها'
            />
          </FormGroup>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant='outlined' color='secondary' onClick={handleCloseEditAdminModal}>
              لغو
            </Button>
            <Button variant='contained' color='primary' onClick={handleUpdateAdmin} disabled={adminSaveLoading}>
              {adminSaveLoading ? <CircularProgress size={24} /> : 'ذخیره'}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Paper className='admin-panel-container'>
        <Box className='admin-panel-header'>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Link to={`/?theme=${theme}`}>
              <img src={theme === 'dark' ? logoImage_1 : logoImage_2} alt='لوگوی نئون' style={{ height: '35px' }} />
            </Link>
            <Typography variant='h4' component='h1'>
              پنل مدیریت
            </Typography>
          </Box>
          <Button component={Link} to={`/dashboard?theme=${theme}`} variant='contained' color='primary'>
            بازگشت به پنل کاربری
          </Button>
        </Box>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant='scrollable'
            scrollButtons='auto'
            aria-label='admin panel tabs'
          >
            <Tab label='مدیریت کاربران' icon={<HiOutlineUserGroup />} iconPosition='start' />
            <Tab label='مدیریت پلن‌ها' icon={<HiOutlineDocumentText />} iconPosition='start' />
            <Tab label='مدیریت تخفیف‌ها' icon={<HiOutlineTicket />} iconPosition='start' />
            <Tab label='محتوا و قوانین' icon={<HiOutlineQuestionMarkCircle />} iconPosition='start' />
            <Tab label='تنظیمات' icon={<HiOutlineCog />} iconPosition='start' />
            <Tab label='تنظیمات سایت' icon={<HiOutlineGlobeAlt />} iconPosition='start' />
            <Tab label='حساب‌های تجاری' icon={<HiOutlineCreditCard />} iconPosition='start' />
            <Tab label='صورت‌حساب‌ها' icon={<HiOutlineClipboardList />} iconPosition='start' />
            <Tab label='تیکت‌ها' icon={<HiOutlineTicket />} iconPosition='start' />
          </Tabs>
        </Box>

        {error && (
          <Alert severity='error' sx={{ m: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TabPanel value={tabValue} index={0}>
          <UsersTab
            apiFetch={apiFetch}
            setError={setError}
            allUsers={allUsers}
            usersLoading={usersLoading}
            usersPage={usersPage}
            usersTotalPages={usersTotalPages}
            onPageChange={fetchAllUsers}
            searchResults={searchResults}
            setSearchResults={setSearchResults}
            showNotificationModal={showNotificationModal}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <PlansTab
            apiFetch={apiFetch}
            handleApiCall={handleApiCall}
            showConfirmationModal={showConfirmationModal}
            setError={setError}
            plans={plans}
            setPlans={setPlans}
            planOptions={planOptions}
            setPlanOptions={setPlanOptions}
            allPlanOptions={allPlanOptions}
            loading={loading.plans}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <DiscountsTab
            apiFetch={apiFetch}
            handleApiCall={handleApiCall}
            showConfirmationModal={showConfirmationModal}
            theme={theme}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <ContentTab
            apiFetch={apiFetch}
            handleApiCall={handleApiCall}
            rule={rule}
            setRule={setRule}
            faqs={faqs}
            loadingRule={loading.rule}
            loadingFaqs={loading.faqs}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          <SettingsTab
            apiFetch={apiFetch}
            handleApiCall={handleApiCall}
            showConfirmationModal={showConfirmationModal}
            setError={setError}
            admins={admins}
            loadingAdmins={loading.admins}
            commissionSettings={commissionSettings}
            loadingSettings={loading.settings}
            onEditAdmin={handleOpenEditAdminModal}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={5}>
          <SiteSettingsTab
            apiFetch={apiFetch}
            handleApiCall={handleApiCall}
            siteSettings={siteSettings}
            setSiteSettings={setSiteSettings}
            loading={loading.siteSettings}
            setError={setError}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={6}>
          <TradeAccountsTab
            apiFetch={apiFetch}
            plans={plans}
            setViewSettingsOpen={setViewSettingsOpen}
            setSettingsToView={setSettingsToView}
            setOptionsForView={setOptionsForView}
            setViewLoading={setViewLoading}
            setActivePlanName={setActivePlanName}
            setError={setError}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={7}>
          <InvoicesTab
            apiFetch={apiFetch}
            handleApiCall={handleApiCall}
            showConfirmationModal={showConfirmationModal}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={8}>
          <RenderAdminTickets />
        </TabPanel>
      </Paper>
    </Container>
  )
}

export default AdminPanel
